/**
 * Pure-function helpers for chat UI logic.
 * Extracted here so they can be unit-tested independently of the screen.
 */

import type { ChatMessageResponse } from '@warcarp/api-types'

/** Storage key for the per-game "last seen public chat message id". Shared by
 *  the in-game chat unread tracker (useChatUnread) and the game-list indicator
 *  so both read/write the same marker. */
export const chatSeenKey = (gameId: string) => `chat_seen_${gameId}`

/**
 * Capture the persisted read marker for a newly opened chat session. While
 * storage is still loading (`undefined`), keep the most recent resolved
 * boundary; a stored `null` is meaningful and must replace it.
 */
export function chatSessionSeenBoundary(
  persistedSeenId: string | null | undefined,
  currentBoundary: string | null | undefined,
): string | null | undefined {
  return persistedSeenId === undefined ? currentBoundary : persistedSeenId
}

/**
 * Whether a game-list row should show the unread-chat indicator. Combines the
 * two unread sources the list has WITHOUT loading either thread:
 *   • secret 1:1 messages — `unreadSecretCount` (server-authoritative), and
 *   • public chat — the server's latest message id (`lastChatMessageId`) vs the
 *     viewer's locally-stored last-seen id (`seenChatId`).
 *
 * Public-chat semantics mirror useChatUnread's `hasUnread`:
 *   - `seenChatId === undefined` (storage not loaded yet) → not unread (avoid a
 *     flash before we know).
 *   - `lastChatMessageId === null` (no chat at all) → not unread.
 *   - `seenChatId === null` (loaded, never opened this game's chat) or differs
 *     from the latest id → unread.
 */
export function gameRowHasUnread(args: {
  unreadSecretCount: number | null | undefined
  lastChatMessageId: string | null | undefined
  seenChatId: string | null | undefined
}): boolean {
  const secretUnread = (args.unreadSecretCount ?? 0) > 0
  const publicUnread =
    args.seenChatId !== undefined &&
    !!args.lastChatMessageId &&
    args.seenChatId !== args.lastChatMessageId
  return secretUnread || publicUnread
}

/**
 * Returns the index (into `messages`) of the first unread message,
 * i.e. the first message whose id is strictly greater (by position)
 * than `sessionSeenId`.  Returns -1 when there is nothing to mark.
 *
 * Logic:
 *  - Empty list → -1.
 *  - sessionSeenId null / undefined (either "never opened" or "no messages
 *    when opened") → -1, treat everything as read from the divider perspective.
 *  - Find the last message whose id === sessionSeenId; the first unread
 *    is the element immediately after it.
 *  - If sessionSeenId is not found in the list (e.g. it was the very latest
 *    when opened but all messages since are new) → first message after the
 *    missing id cannot be determined; fall back to -1 to avoid a misplaced
 *    divider.
 */
export function pickFirstUnreadIndex(
  messages: Pick<ChatMessageResponse, 'id'>[],
  sessionSeenId: string | null | undefined,
): number {
  if (messages.length === 0) return -1
  if (sessionSeenId == null) return -1

  // Walk backwards to find the last message that matches sessionSeenId.
  const lastSeenIdx = [...messages]
    .reverse()
    .findIndex((m) => m.id === sessionSeenId)

  if (lastSeenIdx < 0) return -1 // not found — don't place a divider

  const resolvedIdx = messages.length - 1 - lastSeenIdx

  // Only mark as unread if there are messages after the seen one.
  if (resolvedIdx >= messages.length - 1) return -1

  return resolvedIdx + 1
}

/**
 * Number of messages newer than the last-seen id — drives the chat icon's
 * count badge.
 *
 *  - `lastSeenId === undefined` (persisted value not loaded yet) → 0.
 *  - Empty list → 0.
 *  - `lastSeenId === null` (loaded, nothing ever seen) → all messages unread.
 *  - `lastSeenId` not present in the current tail (trimmed / stale) → treat all
 *    as unread rather than under-count.
 *  - Otherwise → the count of messages after the last-seen one.
 */
export function unreadCount(
  messages: Pick<ChatMessageResponse, 'id'>[],
  lastSeenId: string | null | undefined,
): number {
  if (lastSeenId === undefined || messages.length === 0) return 0
  if (lastSeenId === null) return messages.length
  const idx = messages.findIndex((m) => m.id === lastSeenId)
  if (idx === -1) return messages.length
  return messages.length - 1 - idx
}
